// ════════════════════════════════════════════════════════════════════════════
//  ManDana — When Picker  (the date · time chooser inside the Compose sheet)
//  ----------------------------------------------------------------------------
//  A calm takeover surface. Two quiet zones in one scroll:
//     • the DAY  — a month grid in the app's type system, each Gregorian numeral
//                  carrying a whispered Hijri day beneath it. Quick chips up top.
//     • the TIME — a prayer-anchored ribbon, always open: drop the start
//                  relative to the five prayers, or fine-tune by dragging (5-min
//                  snap). Soft duration stepper. Purple stays earned — only the
//                  selection + live values carry colour. There is no "All day"
//                  mode: every record is timed, so the window is unconditional.
//
//  Emits { offset, min, dur } back to ComposeSheet via onChange; the
//  back-chevron / Done commit and return. Reuses mdDateForOffset / mdOffsetForDate
//  / mdHijriParts / MD_PRAYERS already in the theme. Exposes <WhenPicker>.
// ════════════════════════════════════════════════════════════════════════════
const { useState: useSp, useRef: useRp, useEffect: useEp, useMemo: useMp } = React;
const { mdRgba: rgbaP, ink: inkP, mdDateForOffset: dateForP, mdOffsetForDate: offsetForP,
  mdHijriParts: hijriP } = window;
// NOT destructured: MD_PRAYERS is a live getter now (see md-theme.jsx) and prayer
// times differ per day and per location. Pulling it out here would freeze the
// first location's times into the picker for the life of the page.
const PRAYERS_P_FOR = (offset = 0) => window.mdPrayersFor(offset);

const P_MON_L = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const P_DAYSTART = 240; // ribbon spans 4:00 →
const P_DAYEND = 1440; // → 24:00
const P_SNAP = 5;
const P_MAX_DUR = 8 * 60;
const p5 = (m) => Math.round(m / P_SNAP) * P_SNAP;

/* a single clock time → parts, and a few label forms */
function pPart(m) {const mm = (m % 1440 + 1440) % 1440,H = Math.floor(mm / 60),M = mm % 60;return { h: H % 12 || 12, m: String(M).padStart(2, '0'), ap: H >= 12 ? 'PM' : 'AM' };}
function pClock(m) {const t = pPart(m);return `${t.h}:${t.m} ${t.ap}`;}
function pWindow(min, dur) {const a = pPart(min),b = pPart(min + dur);return `${a.h}:${a.m}${a.ap !== b.ap ? ' ' + a.ap : ''} – ${b.h}:${b.m} ${b.ap}`;}
function pDur(d) {const h = Math.floor(d / 60),m = d % 60;return h ? m ? `${h} hr ${m} min` : `${h} hr` : `${m} min`;}

/* ── prayer-anchor chips: drop the start relative to the rhythm of the day ── */
/* Labels are bare prayer names — the “Start after a prayer” header already says
   “after”, so repeating it on every chip is redundant and costs horizontal room.

   AFTER means after. Each anchor is the END of that day's prayer window, taken
   from md-theme's mdPrayerWindowEnd — the single definition every surface draws
   from. This used to add 30 and snap to the NEAREST five-minute boundary, which
   rounds DOWN two minutes in five: a 4:52 Fajr holds its window to 5:25, and the
   chip offered 5:20, so the record started five minutes inside the prayer. Prayer
   is the axis; nothing is scheduled on top of it (tests/prayer-window.test.js). */
function mdPrayerAnchors(offset) {
  const byId = Object.fromEntries(PRAYERS_P_FOR(offset).map((p) => [p.id, p]));
  const after = (id, label) => (byId[id] ? [{ key: id, label, min: window.mdPrayerWindowEnd(byId[id].min) }] : []);
  return [
    ...after('fajr', 'Fajr'),
    ...after('dhuhr', 'Dhuhr'),
    ...after('asr', 'Asr'),
    ...after('maghrib', 'Maghrib'),
    ...after('isha', 'Isha'),
  ];
}

/* `dateOnly` drops the whole timing zone — the window, the duration stepper,
   the prayer ribbon and the anchor chips — and leaves the calendar. A project's
   target is a DAY: it aims at the 19th, not at 14:30–15:00 on the 19th, and
   asking for a start minute would be asking for a number that means nothing.

   The date half needs no mode of its own. `value.offset` is already a plain
   integer and the month cursor is already seeded from it below, so an offset
   forty days out opens on the right month unaided. This flag only subtracts. */
function WhenPicker({ accent, light, value, onChange, onConfirm, onDiscard, showAnchors = true, weekStart = 'Sat', topRadius = 26, contained = false, dateOnly = false, title = 'Schedule', safeArea = false }) {
  const off = value.offset || 0;
  const surfaceRef = useRp(null);
  const ribbonRef = useRp(null);
  const wrapRef = useRp(null);
  const dragRef = useRp(null);
  const scrollAnim = useRp(null);
  const [surfaceW, setSurfaceW] = useSp(0);

  useEp(() => {
    const el = surfaceRef.current;
    if (!el) return;
    const measure = () => setSurfaceW(Math.round(el.getBoundingClientRect().width));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const wide = !dateOnly && surfaceW >= 760;
  const roomyCalendar = surfaceW >= 620;
  const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const wsIdx = { Sun: 0, Mon: 1, Sat: 6 }[weekStart] != null ? { Sun: 0, Mon: 1, Sat: 6 }[weekStart] : 6;
  const dowHeader = Array.from({ length: 7 }, (_, i) => WD[(wsIdx + i) % 7]);
  const colFor = (jsDay) => (jsDay - wsIdx + 7) % 7;

  const seed = dateForP(off);
  const [cursor, setCursor] = useSp({ y: seed.year, m: seed.m });
  useEp(() => {const d = dateForP(value.offset || 0);setCursor({ y: d.year, m: d.m });}, [value.offset]);

  const monthRows = useMp(() => {
    const { y, m } = cursor;
    const days = new Date(y, m + 1, 0).getDate();
    const lead = colFor(new Date(y, m, 1).getDay());
    const cells = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= days; d++) {
      const o = offsetForP(y, m, d);
      const hp = hijriP(o);
      cells.push({ d, offset: o, hijriDay: hp.day, hijriShort: hp.short, monthStart: hp.isMonthStart, isToday: o === 0 });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    const rows = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [cursor, wsIdx]);

  const set = (patch) => onChange({ ...value, ...patch });
  const pickDay = (o) => set({ offset: o });
  const shiftMonth = (delta) => setCursor((c) => {const d = new Date(c.y, c.m + delta, 1);return { y: d.getFullYear(), m: d.getMonth() };});
  const dayCircle = window.mdDayCircle || (() => ({ bg: 'transparent', numCol: inkP(0.82), fw: 550, shadow: 'none', bd: 'none' }));
  const cellSize = roomyCalendar ? 36 : 30;

  const prayersForDay = PRAYERS_P_FOR(off);
  const closingFajrP = PRAYERS_P_FOR(off + 1).find((p) => p.id === 'fajr');
  const fajrMin = closingFajrP ? closingFajrP.min : 282;
  const DAY_START = P_DAYSTART;
  const DAY_END = 1440 + fajrMin;
  const span = DAY_END - DAY_START;
  const ribbonPx = Math.round(span * (wide ? 0.52 : 0.62));
  const clampMin = (m, d) => Math.min(DAY_END - d, Math.max(DAY_START, m));
  const xToMin = (clientX) => {
    const r = ribbonRef.current.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    return p5(DAY_START + frac * span);
  };
  const onRibbonDown = (e) => {
    e.preventDefault();
    try {e.currentTarget.setPointerCapture(e.pointerId);} catch (err) {}
    dragRef.current = { pid: e.pointerId };
    set({ min: clampMin(xToMin(e.clientX), value.dur || 30) });
  };
  const onRibbonMove = (e) => {
    if (!dragRef.current || e.pointerId !== dragRef.current.pid) return;
    set({ min: clampMin(xToMin(e.clientX), value.dur || 30) });
  };
  const onRibbonUp = (e) => {if (dragRef.current && e.pointerId === dragRef.current.pid) dragRef.current = null;};

  const dur = value.dur || 30;
  const min = value.min == null ? 540 : value.min;
  const stepDur = (delta) => {
    const nextDur = Math.min(P_MAX_DUR, Math.max(15, dur + delta));
    set({ dur: nextDur, min: clampMin(min, nextDur) });
  };
  const leftPct = (m) => (m - DAY_START) / span * 100;
  const ticks = [...prayersForDay.filter((p) => p.min >= DAY_START && p.min <= DAY_END).map((p) => ({ id: p.id, name: p.name, min: p.min })),
  { id: 'fajr2', name: 'Fajr', min: fajrMin + 1440 }].filter((t) => t.min <= DAY_END);

  useEp(() => {
    const w = wrapRef.current;if (!w) return;
    if (wide) {w.scrollLeft = 0;return;}
    w.scrollLeft = Math.max(0, (min - DAY_START) / span * ribbonPx - w.clientWidth * 0.38);
    // Re-centre once when the surface changes between stacked and split form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wide, off]);

  const scrollRibbonTo = (m) => {
    const w = wrapRef.current;if (!w) return;
    if (wide) return;
    const target = Math.max(0, (m - DAY_START) / span * ribbonPx - w.clientWidth * 0.38);
    const start = w.scrollLeft, dist = target - start, t0 = performance.now(), animDur = 380;
    const ease = (p) => 1 - Math.pow(1 - p, 3);
    if (scrollAnim.current) cancelAnimationFrame(scrollAnim.current);
    const step = (now) => {
      const p = Math.min(1, (now - t0) / animDur);
      w.scrollLeft = start + dist * ease(p);
      if (p < 1) scrollAnim.current = requestAnimationFrame(step);
    };
    scrollAnim.current = requestAnimationFrame(step);
  };
  const moveWindow = (delta) => {
    const moved = clampMin(min + delta, dur);
    set({ min: moved });
    scrollRibbonTo(moved);
  };

  const iconButton = {
    width: 40, height: 40, borderRadius: 'var(--md-radius-control)', border: 'none', background: 'none',
    cursor: 'pointer', display: 'grid', placeItems: 'center', padding: 0,
  };
  const stepButton = {
    width: 36, height: 36, borderRadius: 'var(--md-radius-control)', border: 'none', background: 'transparent',
    cursor: 'pointer', display: 'grid', placeItems: 'center', padding: 0,
  };
  const hairline = light ? 'rgba(30,34,42,0.09)' : inkP(0.08);

  const calendar = (
    <div style={{ width: '100%', maxWidth: dateOnly ? 480 : 440, marginInline: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 0 18px' }}>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
          <span style={{ color: inkP(0.94), fontSize: roomyCalendar ? 17 : 15, fontWeight: 680 }}>{P_MON_L[cursor.m]}</span>
          <span style={{ color: inkP(0.48), fontSize: roomyCalendar ? 15 : 14.5, fontWeight: 620 }}>{cursor.y}</span>
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <button aria-label="Previous month" onClick={() => shiftMonth(-1)} style={iconButton}>
            <span style={{ display: 'grid', transform: 'rotate(180deg)' }}>{window.MDI.chevron(inkP(0.55), 11)}</span>
          </button>
          <button aria-label="Next month" onClick={() => shiftMonth(1)} style={iconButton}>
            {window.MDI.chevron(inkP(0.55), 11)}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 8 }}>
        {dowHeader.map((dow, i) =>
        <span key={i} style={{ textAlign: 'center', color: inkP(0.44), fontSize: 11, fontWeight: 600 }}>{dow}</span>)}
      </div>

      {monthRows.map((row, ri) =>
      <div key={ri} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: roomyCalendar ? 5 : 2 }}>
        {row.map((cell, ci) => {
          if (!cell) return <span key={ci} />;
          const sel = cell.offset === off;
          const c = dayCircle(sel, cell.isToday, light, accent);
          const hStart = cell.monthStart;
          return (
            <button key={ci} onClick={() => pickDay(cell.offset)}
            style={{ minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              background: 'none', border: 'none', padding: '3px 0 5px', cursor: 'pointer' }}>
              <div style={{ width: cellSize, height: cellSize, borderRadius: '50%', display: 'grid', placeItems: 'center',
                backgroundColor: c.bg, color: c.numCol, border: c.bd, fontSize: roomyCalendar ? 15 : 14.5, fontWeight: c.fw,
                fontVariantNumeric: 'tabular-nums', transition: 'background-color .18s ease, box-shadow .18s ease, color .18s ease',
                boxShadow: c.shadow }}>{cell.d}</div>
              <span style={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.1, whiteSpace: 'nowrap',
                fontSize: hStart ? 9.5 : 10.5, fontWeight: hStart ? 700 : 500,
                color: hStart ? rgbaP(accent, 0.95) : sel || cell.isToday ? inkP(0.58) : inkP(0.42) }}>{hStart ? cell.hijriShort : cell.hijriDay}</span>
            </button>);
        })}
      </div>)}
    </div>
  );

  const timing = !dateOnly && (
    <div style={{ width: '100%', maxWidth: 560, marginInline: 'auto', display: 'flex', flexDirection: 'column',
      justifyContent: wide ? 'center' : 'flex-start', minHeight: wide ? '100%' : 0 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'end',
        columnGap: 16, marginBottom: wide ? 20 : 18 }}>
        <div style={{ minWidth: 0 }}>
          <div className="md-eyebrow" style={{ color: inkP(0.46), marginBottom: 8 }}>Window</div>
          <span style={{ display: 'block', fontSize: wide ? 21 : 18, lineHeight: 1.1, fontWeight: 650,
            color: rgbaP(accent, 1), fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
            overflow: 'hidden', textOverflow: 'ellipsis' }}>{pWindow(min, dur)}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <button aria-label="Shorter" onClick={() => stepDur(-15)} style={{ ...stepButton, width: 40, height: 40 }}>{window.MDI.minus(inkP(0.66), 16)}</button>
          <span style={{ minWidth: 54, textAlign: 'center', fontSize: 12.5, fontWeight: 650, color: inkP(0.78),
            fontVariantNumeric: 'tabular-nums' }}>{pDur(dur)}</span>
          <button aria-label="Longer" onClick={() => stepDur(15)} style={{ ...stepButton, width: 40, height: 40 }}>{window.MDI.plus(inkP(0.66), 16)}</button>
        </div>
      </div>

      <div ref={wrapRef} className="md-scroll" style={{ overflowX: 'auto', overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch', marginInline: wide ? -4 : 0,
        userSelect: 'none', WebkitUserSelect: 'none' }}>
        <div ref={ribbonRef} onPointerDown={onRibbonDown} onPointerMove={onRibbonMove}
        onPointerUp={onRibbonUp} onPointerCancel={onRibbonUp}
        style={{ position: 'relative', height: wide ? 96 : 88, width: wide ? '100%' : ribbonPx,
          cursor: 'pointer', touchAction: 'none' }}>
          <div style={{ position: 'absolute', left: 10, right: 10, top: wide ? 70 : 62, height: 1,
            background: inkP(light ? 0.14 : 0.13) }} />
          {ticks.map((p, i) =>
          <div key={p.id} style={{ position: 'absolute', top: 0, bottom: 0,
            left: p.id === 'fajr2' ? 'calc(100% - 10px)' : `${leftPct(p.min)}%`, width: 0 }}>
            <span style={{ position: 'absolute', top: i % 2 ? (wide ? 30 : 26) : (wide ? 10 : 8), left: 0,
              transform: p.id === 'fajr2' ? 'translateX(-100%)' : 'translateX(-50%)',
              fontSize: 10.5, fontWeight: 600, color: inkP(0.5), whiteSpace: 'nowrap' }}>{p.name}</span>
            <span style={{ position: 'absolute', top: wide ? 49 : 43, height: wide ? 15 : 13, width: 1,
              background: inkP(0.14) }} />
            <span style={{ position: 'absolute', top: wide ? 66 : 58, left: -4, width: 8, height: 8,
              borderRadius: '50%', background: light ? '#fff' : '#17191f', border: `1.5px solid ${rgbaP(accent, 0.62)}` }} />
          </div>)}
          <div style={{ position: 'absolute', top: wide ? 68.5 : 60.5, height: 4, left: `${leftPct(min)}%`,
            width: `${dur / span * 100}%`, minWidth: 12, borderRadius: 4, background: rgbaP(accent, 0.96),
            boxShadow: `0 0 14px ${rgbaP(accent, light ? 0.24 : 0.38)}` }} />
          <span style={{ position: 'absolute', top: wide ? 63 : 55, left: `${leftPct(min)}%`, width: 15, height: 15,
            marginLeft: -7.5, borderRadius: '50%', background: light ? '#fff' : '#f7f7f8',
            border: `3px solid ${rgbaP(accent, 1)}`, boxShadow: light ? '0 2px 7px rgba(24,30,42,0.18)' : '0 2px 9px rgba(0,0,0,0.5)' }} />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: wide ? 3 : 5 }}>
        <div aria-label="Move window" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <button aria-label="Move 5 minutes earlier" onClick={() => moveWindow(-5)}
            style={{ ...stepButton, width: 40, height: 36 }}>
            {window.MDI.back(inkP(0.55), 15)}
          </button>
          <button aria-label="Move 5 minutes later" onClick={() => moveWindow(5)}
            style={{ ...stepButton, width: 40, height: 36 }}>
            <span style={{ display: 'grid', transform: 'rotate(180deg)' }}>{window.MDI.back(inkP(0.55), 15)}</span>
          </button>
        </div>
      </div>

      {showAnchors &&
      <div style={{ marginTop: wide ? 17 : 15 }}>
        <div className="md-eyebrow" style={{ color: inkP(0.46), marginBottom: 10 }}>Start after a prayer</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', borderTop: `0.75px solid ${hairline}`,
          borderBottom: `0.75px solid ${hairline}` }}>
          {mdPrayerAnchors(off).map((a) => {
            const on = Math.abs((value.min == null ? -999 : value.min) - a.min) < 3;
            return (
              <button key={a.key} onClick={() => { const m = clampMin(a.min, dur); set({ min: m }); scrollRibbonTo(m); }}
              style={{ position: 'relative', minWidth: 0, height: 46, padding: '0 3px', border: 'none', cursor: 'pointer',
                background: on ? rgbaP(accent, light ? 0.09 : 0.14) : 'transparent', color: on ? rgbaP(accent, 1) : inkP(0.62),
                fontSize: 12.5, fontWeight: on ? 700 : 580, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                transition: 'background .16s ease, color .16s ease' }}>
                {on && <span style={{ position: 'absolute', left: '50%', bottom: 5, width: 4, height: 4,
                  transform: 'translateX(-50%)', borderRadius: '50%', background: rgbaP(accent, 1) }} />}
                {a.label}
              </button>);
          })}
        </div>
      </div>}
    </div>
  );

  return (
    <div ref={surfaceRef} style={{ position: 'absolute', inset: 0, zIndex: 9, display: 'flex', flexDirection: 'column',
      overflow: 'hidden', borderRadius: contained ? 'inherit' : `${topRadius}px ${topRadius}px 0 0`,
      background: light ? 'rgba(248,249,250,0.78)' : 'rgba(16,18,22,0.76)',
      backdropFilter: 'blur(30px) saturate(125%)', WebkitBackdropFilter: 'blur(30px) saturate(125%)' }}>
      {/* In an installed iPhone PWA the sheet extends behind the status bar.
          Spend that inset in the header before placing any controls; otherwise
          the close button lands beneath the battery/status icons. Floating
          landscape and desktop panels sit inside the viewport and keep their
          original compact header. */}
      <div style={{ flexShrink: 0, display: 'grid', gridTemplateColumns: '40px minmax(0, 1fr) 40px', alignItems: 'center',
        minHeight: 62, padding: safeArea ? 'var(--md-top-inset) 14px 10px' : '10px 14px',
        borderBottom: `0.75px solid ${hairline}` }}>
        <span aria-hidden="true" />
        <span style={{ textAlign: 'center', fontSize: 16, fontWeight: 700, color: inkP(0.9) }}>{title}</span>
        <window.MdDismiss label="Discard schedule changes" onClick={onDiscard}
          style={{ justifySelf: 'center' }} />
      </div>

      {wide ?
      <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'minmax(350px, 0.88fr) minmax(430px, 1.12fr)' }}>
        <div className="md-scroll" style={{ overflowY: 'auto', padding: '30px clamp(24px, 4vw, 46px) 34px' }}>{calendar}</div>
        <div className="md-scroll" style={{ overflowY: 'auto', padding: '30px clamp(28px, 4vw, 54px) 88px',
          borderInlineStart: `0.75px solid ${hairline}`, background: light ? 'rgba(235,238,242,0.42)' : 'rgba(255,255,255,0.022)' }}>{timing}</div>
      </div> :
      <div className="md-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: dateOnly ? '26px 24px 94px' : '18px 18px 94px' }}>
        {calendar}
        {timing && <div style={{ marginTop: 32, paddingTop: 26, borderTop: `0.75px solid ${hairline}` }}>{timing}</div>}
      </div>}
      <button aria-label="Confirm schedule" onClick={onConfirm}
        style={{ position: 'absolute', zIndex: 14, insetInlineEnd: 22, bottom: 22, width: 50, height: 50,
          borderRadius: '50%', border: 'none', cursor: 'pointer', display: 'grid', placeItems: 'center', padding: 0,
          background: rgbaP(accent, 1), boxShadow: `0 8px 24px ${rgbaP(accent, light ? 0.32 : 0.42)}`,
          transition: 'transform .16s cubic-bezier(.2,.8,.2,1), box-shadow .16s ease' }}
        onPointerDown={(e) => e.currentTarget.style.transform = 'scale(0.88)'}
        onPointerUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
        onPointerLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
        {window.MDI.check(window.onAcc(), 24)}
      </button>
    </div>);
}

/* The picker has one presentation everywhere it is invoked. Landscape gets a
   bounded popover that preserves the record or day as context; narrow portrait
   screens get an inset bottom sheet. Both use the same glass, spacing and exit. */
function ScheduleOverlay({ accent, light, value, onChange, onConfirm, onDiscard, showAnchors = true, weekStart = 'Sat', dateOnly = false, title = 'Schedule' }) {
  const hostRef = useRp(null);
  const timerRef = useRp(null);
  const closingRef = useRp(false);
  const [host, setHost] = useSp({ w: 0, h: 0 });
  const [entered, setEntered] = useSp(false);
  const [leaving, setLeaving] = useSp(false);

  useEp(() => {
    const el = hostRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setHost({ w: Math.round(r.width), h: Math.round(r.height) });
    };
    measure();
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setEntered(true)));
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (ro) ro.observe(el);
    return () => {cancelAnimationFrame(raf);if (ro) ro.disconnect();clearTimeout(timerRef.current);};
  }, []);

  // Presentation follows the viewport, never the component that happened to
  // open it. That keeps compose and day rescheduling literally the same size.
  const floating = host.w >= 760 && host.h >= 540 && (host.w > host.h || host.w >= 1100);
  const shown = entered && !leaving;
  const finish = (action) => {
    if (closingRef.current) return;
    closingRef.current = true;
    setLeaving(true);
    timerRef.current = setTimeout(action, 240);
  };
  const confirm = () => finish(onConfirm);
  const discard = () => finish(onDiscard);
  /* Seated on the scene as 'datepicker' (N-9): Back discards the picker and
     the sheet that asked for it stands untouched. */
  window.useMdDismissLayer(discard, !leaving, null, 'datepicker');

  const panelStyle = floating ? {
    position: 'relative', width: 'min(840px, calc(100% - 40px))', height: 'min(640px, calc(100% - 40px))',
    borderRadius: 'var(--md-radius-sheet)', overflow: 'hidden',
    opacity: shown ? 1 : 0,
    transform: shown ? 'translateY(0) scale(1)' : 'translateY(8px) scale(.94,.97)',
    animation: shown ? 'mdScheduleSqueezeIn .48s cubic-bezier(.2,.78,.2,1) both' : 'none',
  } : {
    position: 'relative', width: 'calc(100% - 16px)', height: 'min(820px, calc(100% - 12px))',
    marginBottom: 8, borderRadius: 'var(--md-radius-sheet)', overflow: 'hidden',
    opacity: shown ? 1 : 0, transform: shown ? 'translateY(0) scale(1)' : 'translateY(26px) scale(.97,.985)',
    animation: shown ? 'mdScheduleSqueezeIn .48s cubic-bezier(.2,.78,.2,1) both' : 'none',
  };

  const overlay = (
    <div ref={hostRef} style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex',
      userSelect: 'none', WebkitUserSelect: 'none',
      alignItems: floating ? 'center' : 'flex-end', justifyContent: 'center' }}>
      <div onClick={discard} aria-hidden="true" style={{ position: 'absolute', inset: 0,
        background: light ? 'rgba(28,32,40,0.11)' : 'rgba(0,0,0,0.34)',
        backdropFilter: 'blur(7px)', WebkitBackdropFilter: 'blur(7px)',
        opacity: shown ? 1 : 0, transition: 'opacity .24s ease' }} />
      <div role="dialog" aria-modal="true" aria-label={title} style={{ ...panelStyle,
        border: light ? '0.75px solid rgba(255,255,255,0.84)' : `0.75px solid ${inkP(0.13)}`,
        boxShadow: light
          ? '0 26px 80px rgba(28,34,46,0.19), inset 0 1px rgba(255,255,255,0.72)'
          : '0 30px 90px rgba(0,0,0,0.58), inset 0 1px rgba(255,255,255,0.055)',
        transition: leaving ? 'opacity .2s ease, transform .24s cubic-bezier(.4,0,.7,.2)' : 'none',
        transformOrigin: '50% 52%', willChange: 'transform, opacity' }}>
        <WhenPicker accent={accent} light={light} value={value} onChange={onChange}
          onConfirm={confirm} onDiscard={discard}
          showAnchors={showAnchors} weekStart={weekStart} contained dateOnly={dateOnly} title={title}
          safeArea={!floating} />
      </div>
    </div>);

  return ReactDOM.createPortal(overlay, document.body);
}

/* mdPrayerAnchors is exported for the contract test, not for another surface:
   “after a prayer” must agree with the day's windows, and that is provable
   without a browser (tests/prayer-window.test.js). */
Object.assign(window, { WhenPicker, ScheduleOverlay, mdPrayerAnchors });
